import { captureException } from "@sentry/react"
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { PassForSale } from "ipctg-inflow-types/InFlowAPIMisc"
import { useLayoutEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/globalComponents/atoms/Button"
import Heading from "@/globalComponents/atoms/Heading"
import Icon from "@/globalComponents/atoms/Icon"
import Text from "@/globalComponents/atoms/Text"
import ProductDetails, {
  ProductSpecifications,
} from "@/globalComponents/molecules/productCards/ProductDetails"
import { QuotaPackCard } from "@/globalComponents/molecules/productCards/QuotaPack"
import { formatDateForDisplay, getLocale } from "@/utilities/etc"
import { flexPurchaseCheckoutQueryOptions } from "@/utilities/hooks/queries/park"
import { passesQueryOptions } from "@/utilities/hooks/queries/passes"
import { useTheme } from "@/utilities/hooks/useTheme"
import { log } from "@/utilities/logging"
import { prettyCurrencyNumber } from "@/utilities/normalizers"
import { getPassSessionLengthCopy } from "@/utilities/passes"

import NotRedeemableWarningModal from "../-components/NotRedeemableWarningModal"
import { PassPassphraseModal } from "../-components/PassPassphraseModal"

export const Route = createFileRoute("/qr/$qrLookupID/passes/$epoch/purchase/")(
  {
    staticData: { pageName: "Select Pass" },
    component: RouteComponent,
  }
)

function RouteComponent() {
  const navigate = Route.useNavigate()
  const queryClient = useQueryClient()
  const { qrLookupID, epoch } = Route.useParams()
  const { scannedArea } = useTheme()
  const { t, i18n } = useTranslation("passes")
  const locationName =
    scannedArea?.theme?.locationInformation.locationName || "Demo Location"

  const { data: passesData } = useSuspenseQuery(passesQueryOptions())
  const [warningModalRateID, setWarningModalRateID] = useState<string | null>(
    null
  )
  const [passphrasePassState, setPassphrasePassState] = useState<{
    rateID: string
    passName: string
  } | null>(null)

  useLayoutEffect(() => {
    const element = document.getElementsByTagName("main")
    if (!element.length) return
    element[0].style.maxWidth = "27rem"

    return () => {
      if (!element.length) return
      element[0].style.maxWidth = ""
    }
  })

  return (
    <>
      <Heading level={2} as={1} className="center">
        {t("purchase.index.header")}
      </Heading>
      <Text level={2} className="center">
        {t("purchase.index.description", { locationName })}
      </Text>
      {passesData.passesForSale.map((pass) => {
        return (
          <article key={pass.rateProductID} className="module">
            <QuotaPackCard pass={pass} />
            <ProductDetails details={getProductSpecifications(pass)} />
            {pass.showNotRedeemableWarning && (
              <div className="warning-card">
                <Icon name="warning" />
                <Text level={3} className="medium">
                  {t("purchase.index.warnings.notRedeemableWarning")}
                </Text>
              </div>
            )}
            <Button buttonType="primary" onPress={() => handlePassClick(pass)}>
              {t("purchase.index.card.action", {
                price: prettyCurrencyNumber(pass.amount),
              })}
            </Button>
          </article>
        )
      })}

      <NotRedeemableWarningModal
        isOpen={warningModalRateID !== null}
        onOpenChange={(isOpen: boolean) => {
          if (!isOpen) setWarningModalRateID(null)
        }}
        onProceed={() => {
          const pass = passesData.passesForSale.find(
            (p) => p.rateProductID === warningModalRateID
          )
          if (pass) {
            handlePassClick(pass)
          }
          setWarningModalRateID(null)
        }}
      />

      {/* Passphrase Modal */}
      {passphrasePassState && (
        <PassPassphraseModal
          isOpen={true}
          onOpenChange={(isOpen: boolean) => {
            if (!isOpen) setPassphrasePassState(null)
          }}
          rateID={passphrasePassState.rateID}
          passName={passphrasePassState.passName}
          onSuccess={() => goToCart(passphrasePassState.rateID)}
        />
      )}
    </>
  )

  function getProductSpecifications(pass: PassForSale): ProductSpecifications {
    if (pass.passSpecifications) {
      return [
        {
          icon: "pass",
          copy: t("purchase.index.card.specifications.quotaPack.quota", {
            count: pass.passSpecifications.maxUses,
            date: formatDateForDisplay({
              isoDateString: pass.passSpecifications.validTo,
              locale: i18n.resolvedLanguage || getLocale(),
            }),
          }),
        },
        {
          icon: "clock",
          copy: getPassSessionLengthCopy({
            redemptionConfig: pass.passSpecifications.redemptionConfig,
            locale: i18n.resolvedLanguage,
            t,
            mode: "purchase",
          }),
        },
        {
          icon: "disco",
          copy: pass.passSpecifications.subjectToEventRestrictions
            ? t(
                "purchase.index.card.specifications.common.subjectToEventRestrictions"
              )
            : "",
        },
        {
          icon: "lock",
          copy: pass.passSpecifications.requiresPassphrase
            ? t("purchase.index.card.specifications.common.requiresPassphrase")
            : "",
        },
      ]
    } else if (pass.passNotes) {
      return pass.passNotes
    } else {
      // TODO log to sentry with the passID
      log({
        level: "error",
        flow: "passes:purchase",
        scope: "misconfiguration",
        message: "No expected pass details found",
        meta: { pass },
      })
      captureException(new Error("Possible pass misconfiguration"), {
        extra: { pass, message: "Missing expected specifications formats" },
      })
      return []
    }
  }

  function handlePassClick(pass: PassForSale) {
    if (pass.passSpecifications?.requiresPassphrase) {
      setPassphrasePassState({
        rateID: pass.rateProductID,
        passName: pass.passName,
      })
    } else {
      goToCart(pass.rateProductID)
    }
  }

  function goToCart(rateID: string) {
    queryClient.removeQueries(
      flexPurchaseCheckoutQueryOptions({ rateID, epoch })
    )
    navigate({
      to: "/qr/$qrLookupID/passes/$epoch/purchase/cart",
      params: { qrLookupID, epoch },
      search: {
        rateID,
      },
    })
  }
}
